import React, { useState, useEffect, useRef, useCallback } from "react";
import { createErpSocket } from "../utils/socketIo";
import {
  formatIndianRupee,
  formatFooterDateTime,
} from "../utils/formatLiveRate";
import AnalyticsTracker from "../components/AnalyticsTracker";
import { trackEvent, getTenantName } from "../services/analytics";

import {
  getUserData,
  encryptData,
  setUserPermissions,
  node_imageBaseUrl,
} from "./const";
import {
  getErpToken,
  getErpAuthState,
  clearErpAuthState,
  getFinancialYear,
  setFinancialYear,
  listFiscalYears,
  applyRealtimeSessionFromAdminUserSocket,
  getErpBranchDetails,
  getActiveBranchId,
  setActiveBranchId,
  fetchErpMe,
} from "../erpClient";
import AccountDeactivatedModal from "../components/AccountDeactivatedModal";
import SignUp from "./SignUp";
import Login from "./Login";
import OTP from "./OTP";
import AccessPIN from "./AccessPIN";
import ForgetPassword from "./ForgetPassword";
import CustomerForm from "./CustomerForm";
import CustHeader from "./CustHeader";
import Sidebar from "./Sidebar";
import Navbar from "./Navbar";
import CustomerAssignTable from "./CustomerAssignTable";
import BillTypeTable from "./BillTypeTable";
import { ShopExpenseRoute } from "./ShopExpense";
import SalesTarget from "./SalesTarget";
import { HrmsPage } from "./HrmsPage";
import Dashboard from "./Dashboard";
import BankDetailsTable from "./BankDetailsTable";
import MetalGroupTable from "./MetalGroupTable";
import ItemCategoryTable from "./ItemCategoryTable";
import RoleTable from "./RoleTable";
import AddRole from "./AddRole";
import EditRole from "./EditRole";
import SupplierTable from "./SupplierTable";
import CustomerTable from "./CustomerTable";
import ProductGroupTable from "./ProductGroupTable";
import ProductAssortingTable from "./ProductAssortingTable";
import StockCheck from "./StockCheck";
import AddStock from "./AddStock";
import BulkStock from "./BulkStock";
import StockTable from "./StockTable";
import EditStock from "./EditStock";
import AddPurchase from "./AddPurchase";
import PurchaseRateCut from "./AddPurchaseRateCut";
import EditPurchase from "./EditPurchase";
import PurchaseTable from "./PurchaseTable";
import MetalPurchaseTable from "./MetalPurchaseTable";
import MetalTransfer from "./MetalTransfer";
import RefineryTable from "./RefineryTable";
import AddRefinery from "./AddRefinery";
import EditRefinery from "./EditRefinery";
import ViewRefinery from "./ViewRefinery";
import AddOldPurchase from "./AddOldPurchase";
import EditOldPurchase from "./EditOldPurchase";
import OldPurchasePDFView from "./OldPurchasePDFView";
import Sales from "./Sales";
import Invoice from "./Invoice";
import DailyPrice from "./DailyPrice";
import TestCustomerForm from "./TestCustomerForm";
import Karigar from "./Karigar";
import CustomOrderEntry from "./CustomOrderEntry";
import AddSettlement from "./AddSettlement";
import CustomOrderEntryTable from "./CustomOrderEntryTable";
import EditCustomOrderEntry from "./EditCustomOrderEntry";
import OrderQueue from "./OrderQueue";
import RepairingOrder from "./RepairingOrder";
import AddRepairingOrder from "./AddRepairingOrder";
import EditRepairingOrder from "./EditRepairingOrder";
import RepairingSales from "./RepairingSales";
import JangadTable from "./JangadTable";
import AddJangad from "./AddJangad";
import EditJangad from "./EditJangad";
import AddGirvi from "./girvi/AddGirvi";
import GirviBankDetails from "./girvi/GirviBankDetails";
import GirviDashboard from "./girvi/GirviDashboard";
import GirviTable from "./girvi/GirviTable";
import GirviQuickPayment from "./girvi/GirviQuickPayment";
import GirviPaymentEntry from "./girvi/GirviPaymentEntry";
import EditGirvi from "./girvi/EditGirvi";
import PrintBarcode from "./PrintBarcode";
import HuidBarcodeSearch from "./HuidBarcodeSearch";
import CapitalInvestmentTable from "./CapitalInvestmentTable";
import TenantBranchTable from "./TenantBranchTable";
import EmployeeSelfService from "./EmployeeSelfService";
import WhatsAppInbox from "./whatsapp/WhatsAppInbox";
import WhatsAppPhonebooks from "./whatsapp/WhatsAppPhonebooks";
import WhatsAppTemplateCreate from "./whatsapp/WhatsAppTemplateCreate";
import WhatsAppTemplates from "./whatsapp/WhatsAppTemplates";
import WhatsAppSendCampaign from "./whatsapp/WhatsAppSendCampaign";
import WhatsAppCampaigns from "./whatsapp/WhatsAppCampaigns";
import WhatsAppCampaignDetail from "./whatsapp/WhatsAppCampaignDetail";
import WhatsAppDashboard from "./whatsapp/WhatsAppDashboard";
import WhatsAppRootRedirect from "./whatsapp/WhatsAppRootRedirect";
import WhatsAppRoute from "../components/WhatsAppRoute";
import { WHATSAPP_PERMISSION_CODES } from "../utils/whatsappPermissions";

import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import ProtectedRoute from "../components/ProtectedRoute";
import Employee from "./Employee";
import EditEmployee from "./EditEmployee";
import AdminApproval from "./AdminApproval";
import PermissionRestriced from "./PermissionRestriced";
import {
  getUserPermissions,
  PERMISSIONS,
  HRMS_ROUTE_ANY_PERMISSIONS,
  ROJMEL_ROUTE_ANY_PERMISSIONS,
} from "../utils/permissions";
import MyProfile from "./MyProfile";
import EditInvoice from "./EditInvoice";
import KarigarLedger from "./KarigarLedger";
import BankLedger from "./BankLedger";
import CustomerLedger from "./CustomerLedger";
import SalesLedger from "./SalesLedger";
import PurchaseLedger from "./PurchaseLedger";
import OverallLedger from "./OverallLedger";
import Rojmel from "./Rojmel";
import UrdLedger from "./UrdLedger";
import IdleTimeoutWrapper from "../components/IdleTimeoutWrapper";
import { IdleProvider, useIdleContext } from "../context/IdleContext";
import LeadsTable from "./LeadsTable";
import LeadDetail from "./LeadDetail";
import FollowUps from "./FollowUps";
import RemindersQueue from "./RemindersQueue";
import LanguageSelector from "../components/LanguageSelector";
import FooterFinancialYearControl from "../components/FooterFinancialYearControl";
import FooterLockButton from "../components/FooterLockButton";
import FooterProfileMenu from "../components/FooterProfileMenu";
import FullscreenToggleButton from "../components/FullscreenToggleButton";
import SignOutConfirmModal from "../components/SignOutConfirmModal";
import { useFullscreen } from "../hooks/useFullscreen";
import { performAppSignOut } from "../utils/signOut";
import DesktopKeyboardProvider from "../desktop/context/DesktopKeyboardProvider";

/** Bookmarks / links to `/customorderdetails/:id` open the legacy full edit screen. */
function NavigateCustomOrderDetailToEdit() {
  const { id } = useParams();
  return (
    <Navigate
      to="/editcustomorderentry"
      replace
      state={{ customOrderId: id }}
    />
  );
}

/** Bookmarks / links to `/repairingorderdetails/:id` open the legacy full edit screen. */
function NavigateRepairingOrderDetailToEdit() {
  const { id } = useParams();
  return (
    <Navigate
      to="/editrepairingorder"
      replace
      state={{ repairingOrderId: id }}
    />
  );
}

const MAIN_CONTENT_SCROLL_IDLE_MS = 700;

// Layout component for protected routes with sidebar, navbar, and footer
const MainLayout = ({
  masterGoldRate,
  masterSilverRate,
  showLiveRate,
  onLiveRateToggle,
  lastUpdateTime,
  children,
}) => {
  const [isMiniSidebar, setIsMiniSidebar] = useState(false);
  const [isCompactViewport, setIsCompactViewport] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 1300,
  );
  const sidebarScrollRatioRef = useRef(null);
  const [layoutType, setLayoutType] = useState("sidebar");
  const [footerUserData, setFooterUserData] = useState(null);
  const [footerProfileImage, setFooterProfileImage] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { lockApp } = useIdleContext();
  const handleExitFullscreenViaBrowserBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);
  const { isFullscreen, toggleFullscreen } = useFullscreen({
    onExitViaBrowserBack: handleExitFullscreenViaBrowserBack,
  });
  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(
    () => getFinancialYear() || "",
  );
  const [isFiscalYearLoading, setIsFiscalYearLoading] = useState(false);
  const [footerBranches, setFooterBranches] = useState([]);
  const [footerActiveBranchId, setFooterActiveBranchId] = useState("");
  const [pageDataKey, setPageDataKey] = useState(0);
  const [isSwitchingBranch, setIsSwitchingBranch] = useState(false);
  const mainContentRef = useRef(null);
  const mainContentScrollIdleTimerRef = useRef(null);
  const [isMainContentScrolling, setIsMainContentScrolling] = useState(false);
  const [showSignOutModal, setShowSignOutModal] = useState(false);

  const isSidebarCollapsed = isMiniSidebar && !isCompactViewport;

  const toggleSidebar = (event) => {
    if (!isCompactViewport) {
      event?.stopPropagation?.();
    }

    const scrollEl = document.querySelector(
      ".left-sidebar .scroll-sidebar.sidebar-nav",
    );
    if (scrollEl) {
      const maxScroll = scrollEl.scrollHeight - scrollEl.clientHeight;
      sidebarScrollRatioRef.current =
        maxScroll > 0 ? scrollEl.scrollTop / maxScroll : 0;
    }
    setIsMiniSidebar((prev) => !prev);
  };

  const currentModule = location.pathname.startsWith("/whatsapp")
    ? "WhatsApp"
    : location.pathname.includes("/girvi") ||
      location.pathname.startsWith("/addgirvi") ||
      location.pathname.startsWith("/girvitable") ||
      location.pathname.startsWith("/editgirvi")
      ? "Dihran"
      : "Home";

  useEffect(() => {
    const syncViewport = () => {
      setIsCompactViewport(window.innerWidth < 1300);
    };

    syncViewport();
    window.addEventListener("resize", syncViewport);
    return () => window.removeEventListener("resize", syncViewport);
  }, []);

  useEffect(() => {
    mainContentRef.current?.scrollTo({ top: 0, left: 0 });
  }, [location.pathname, location.search, location.hash]);

  // Icon-only mini rail only at desktop widths (>= 1300px)
  useEffect(() => {
    document.documentElement.setAttribute("data-boxed-layout", "full");

    const sidebarType =
      isCompactViewport || !isMiniSidebar ? "full" : "mini-sidebar";
    document.body.setAttribute("data-sidebartype", sidebarType);

    const ratio = sidebarScrollRatioRef.current;
    if (ratio == null) return undefined;

    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const scrollEl = document.querySelector(
          ".left-sidebar .scroll-sidebar.sidebar-nav",
        );
        if (scrollEl) {
          const maxScroll = Math.max(
            0,
            scrollEl.scrollHeight - scrollEl.clientHeight,
          );
          scrollEl.scrollTop = ratio * maxScroll;
        }
        sidebarScrollRatioRef.current = null;
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [isMiniSidebar, isCompactViewport]);

  useEffect(() => {
    const mainEl = mainContentRef.current;
    if (!mainEl) return undefined;

    const handleScroll = () => {
      setIsMainContentScrolling(true);
      if (mainContentScrollIdleTimerRef.current) {
        clearTimeout(mainContentScrollIdleTimerRef.current);
      }
      mainContentScrollIdleTimerRef.current = setTimeout(() => {
        setIsMainContentScrolling(false);
      }, MAIN_CONTENT_SCROLL_IDLE_MS);
    };

    mainEl.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      mainEl.removeEventListener("scroll", handleScroll);
      if (mainContentScrollIdleTimerRef.current) {
        clearTimeout(mainContentScrollIdleTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    let hideTimer = null;

    const isPremium = () => html.getAttribute("data-theme") === "premium";

    const openDock = () => {
      if (!isPremium()) return;
      html.classList.add("premium-dock-open");
      if (hideTimer) clearTimeout(hideTimer);
      hideTimer = setTimeout(() => {
        const dock = document.querySelector(".left-sidebar");
        if (dock && dock.matches(":hover")) {
          openDock();
          return;
        }
        html.classList.remove("premium-dock-open");
      }, 1800);
    };

    const onScrollOrWheel = () => {
      openDock();
    };

    const onThemeChanged = () => {
      html.classList.remove("premium-dock-open");
    };

    const scrollPanes = [
      mainContentRef.current,
      document.querySelector(".app-main-scroll"),
      document.querySelector(".page-wrapper"),
    ].filter(Boolean);

    document.addEventListener("wheel", onScrollOrWheel, {
      passive: true,
      capture: true,
    });
    document.addEventListener("touchmove", onScrollOrWheel, {
      passive: true,
      capture: true,
    });
    document.addEventListener("scroll", onScrollOrWheel, {
      passive: true,
      capture: true,
    });
    scrollPanes.forEach((el) => {
      el.addEventListener("scroll", onScrollOrWheel, { passive: true });
    });
    document.addEventListener("themeChanged", onThemeChanged);

    return () => {
      document.removeEventListener("wheel", onScrollOrWheel, true);
      document.removeEventListener("touchmove", onScrollOrWheel, true);
      document.removeEventListener("scroll", onScrollOrWheel, true);
      scrollPanes.forEach((el) => {
        el.removeEventListener("scroll", onScrollOrWheel);
      });
      document.removeEventListener("themeChanged", onThemeChanged);
      html.classList.remove("premium-dock-open");
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, []);

  // Enforce sidebar-only layout
  useEffect(() => {
    localStorage.setItem("layoutType", "sidebar");
    setLayoutType("sidebar");
  }, []);

  const showSidebar = layoutType === "sidebar";

  const loadFooterProfile = () => {
    const legacy = getUserData();
    const erpUser = getErpAuthState().user;
    const merged = {
      ...(legacy || {}),
      ...(erpUser || {}),
      name:
        legacy?.name || erpUser?.name || erpUser?.full_name || erpUser?.email,
      phone: legacy?.phone || erpUser?.phone || erpUser?.mobile,
    };

    if (merged?.name || merged?.phone || merged?.mobile || merged?.img) {
      setFooterUserData(merged);
      setFooterProfileImage(
        merged.img ? `${node_imageBaseUrl}${merged.img}` : null,
      );
    } else {
      setFooterUserData(null);
      setFooterProfileImage(null);
    }
  };

  useEffect(() => {
    loadFooterProfile();

    const handleStorageChange = (e) => {
      if (
        e.key === "userData" ||
        e.key === "user-details" ||
        e.key === "api-token" ||
        e.key === null
      ) {
        loadFooterProfile();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("profileImageUpdated", loadFooterProfile);
    window.addEventListener("erpAuthUpdated", loadFooterProfile);
    window.addEventListener("focus", loadFooterProfile);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("profileImageUpdated", loadFooterProfile);
      window.removeEventListener("erpAuthUpdated", loadFooterProfile);
      window.removeEventListener("focus", loadFooterProfile);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadYears = async () => {
      setIsFiscalYearLoading(true);
      try {
        const response = await listFiscalYears();
        const list = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : [];

        const years = list
          .map((year) => String(year || "").trim())
          .filter(Boolean);

        if (!isMounted) return;
        setFiscalYears(years);

        if (years.length === 0) {
          setSelectedFiscalYear("");
          setFinancialYear("");
          return;
        }

        const stored = getFinancialYear();
        const resolved = years.includes(stored) ? stored : years[0];
        setSelectedFiscalYear(resolved);
        setFinancialYear(resolved);
      } catch {
        if (!isMounted) return;
        setFiscalYears([]);
      } finally {
        if (isMounted) setIsFiscalYearLoading(false);
      }
    };

    loadYears();
    return () => {
      isMounted = false;
    };
  }, []);

  const formatFiscalYearShort = (value) => {
    const text = String(value || "").trim();
    const parts = text.split("-");
    if (parts.length === 2) {
      const start = parts[0].trim();
      const end = parts[1].trim();
      return `${start.slice(-2)}-${end.slice(-2)}`;
    }
    return text;
  };

  const handleFiscalYearChange = (value) => {
    const v = String(value || "").trim();
    if (!v || v === String(selectedFiscalYear || "").trim()) return;
    setSelectedFiscalYear(v);
    setFinancialYear(v);
    setPageDataKey((key) => key + 1);
  };

  useEffect(() => {
    const refreshBranches = () => {
      const stored = getErpBranchDetails();
      let list = stored;
      if (!list.length) {
        const user = getErpAuthState()?.user;
        const fromUser = user?.branches ?? user?.branch_details;
        list = Array.isArray(fromUser) ? fromUser : [];
      }
      setFooterBranches(list);
      setFooterActiveBranchId(getActiveBranchId() || "");
    };

    refreshBranches();
    if (getErpBranchDetails().length === 0 && getErpToken()) {
      fetchErpMe()
        .then(refreshBranches)
        .catch(() => {});
    }

    window.addEventListener("permissionsUpdated", refreshBranches);
    window.addEventListener("erpAuthUpdated", refreshBranches);
    return () => {
      window.removeEventListener("permissionsUpdated", refreshBranches);
      window.removeEventListener("erpAuthUpdated", refreshBranches);
    };
  }, []);

  const handleFooterBranchChange = async (event) => {
    const id = Number(event.target.value);
    if (!Number.isFinite(id) || id <= 0) return;
    if (Number(id) === Number(getActiveBranchId())) return;

    setFooterActiveBranchId(id);
    setIsSwitchingBranch(true);
    const switched = setActiveBranchId(id);
    if (!switched) {
      setFooterActiveBranchId(getActiveBranchId() || "");
      setIsSwitchingBranch(false);
      return;
    }

    try {
      await fetchErpMe();
    } catch {
      // Page still reloads against the new branch header.
    }

    if (getActiveBranchId() !== id) setActiveBranchId(id);
    setPageDataKey((key) => key + 1);
    setIsSwitchingBranch(false);
  };

  const handleFooterLock = () => {
    lockApp("manual");
  };

  const handleFooterSignOut = () => {
    setShowSignOutModal(true);
  };

  const confirmFooterSignOut = () => {
    trackEvent("Authentication", "Logout", getTenantName());
    setShowSignOutModal(false);
    performAppSignOut(navigate);
  };

  const footerProfileLabel =
    footerUserData?.name ||
    footerUserData?.phone ||
    footerUserData?.mobile ||
    "Guest User";

  return (
    <>
      <div
        id="main-wrapper"
        className={`${showSidebar ? (isMiniSidebar ? "show-sidebar" : "") : "no-sidebar"
          } ${isFullscreen ? "fullscreen-app" : ""}`}
        style={!showSidebar ? { marginLeft: 0 } : {}}
      >
        {showSidebar && (
          <Sidebar
            currentModule={currentModule}
            isSidebarCollapsed={isSidebarCollapsed}
          />
        )}
        <div
          className="page-wrapper d-flex flex-column vh-100"
          style={!showSidebar ? { marginLeft: 0, width: "100%" } : {}}
        >
          <Navbar
            toggleSidebar={toggleSidebar}
            layoutType={layoutType}
            currentModule={currentModule}
            showActionControls={false}
          />
          <DesktopKeyboardProvider>
            <div
              ref={mainContentRef}
              className={[
                "flex-grow-1",
                "overflow-auto",
                "app-main-scroll",
                "app-scroll--idle-hidden",
                isMainContentScrolling ? "app-scroll--scrolling" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {children ? (
                <div key={pageDataKey} className="h-100">
                  {children}
                </div>
              ) : (
                <Outlet key={pageDataKey} />
              )}
            </div>
          </DesktopKeyboardProvider>

          {/* Footer with Live Rates */}
          <footer className="bg-white border-top py-2 mt-auto app-footer-bar">
            <div className="container-fluid px-1">
              <div className="row g-2 align-items-center flex-wrap">
                {/* <div className="col-auto form-check mb-0 d-flex align-items-center">
                  <input
                    type="checkbox"
                    className="form-check-input me-2"
                    id="liveRateCheck"
                    checked={showLiveRate}
                    onChange={(e) => onLiveRateToggle(e.target.checked)}
                    style={{ width: "1rem", height: "1rem" }}
                  />
                  <label
                    className="form-check-label text-muted small mb-0"
                    htmlFor="liveRateCheck"
                  >
                    Live Rate
                  </label>
                </div> */}
                <div className="col-auto d-flex align-items-center app-footer-rate-text">
                  {Array.isArray(masterGoldRate) ? (
                    <>
                      <span className="text-muted ms-2">
                        24C: {formatIndianRupee(masterGoldRate[0] || 0)}
                      </span>
                      <span className="text-muted ms-2">
                        22C: {formatIndianRupee(masterGoldRate[1] || 0)}
                      </span>
                      <span className="text-muted ms-2">
                        18C: {formatIndianRupee(masterGoldRate[2] || 0)}
                      </span>
                      <span className="text-muted ms-2">
                        18C RG: {formatIndianRupee(masterGoldRate[3] || 0)}
                      </span>
                      <span className="text-muted ms-2">
                        9C:{" "}
                        {formatIndianRupee(
                          masterGoldRate[4] ||
                          (Number(masterGoldRate[0] || 0) * 9) / 24,
                        )}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-muted">Fine Gold:</span>
                      <span className="text-muted ms-2">
                        {formatIndianRupee(masterGoldRate || 0)}
                      </span>
                    </>
                  )}
                </div>
                <div className="col-auto d-flex align-items-center app-footer-rate-text">
                  <span className="text-muted">Silver:</span>
                  <span className="text-muted ms-2">
                    {formatIndianRupee(masterSilverRate || 0)}
                  </span>
                  <span className="text-muted ms-3 text-nowrap">
                    ({formatFooterDateTime(lastUpdateTime)})
                  </span>
                </div>
                <div className="col-auto ms-auto d-flex align-items-center gap-3">
                  <FooterLockButton onLock={handleFooterLock} />

                  <span
                    aria-hidden="true"
                    style={{
                      width: "1px",
                      height: "14px",
                      backgroundColor: "rgba(108, 117, 125, 0.65)",
                    }}
                  />

                  <FullscreenToggleButton
                    isFullscreen={isFullscreen}
                    onToggle={toggleFullscreen}
                  />

                  <span
                    aria-hidden="true"
                    style={{
                      width: "1px",
                      height: "14px",
                      backgroundColor: "rgba(108, 117, 125, 0.65)",
                    }}
                  />

                  {footerBranches.length > 0 && (
                    <select
                      className="border-0 bg-transparent text-muted"
                      style={{
                        width: "auto",
                        maxWidth: "12rem",
                        fieldSizing: "content",
                        fontSize: "0.9rem",
                        lineHeight: 1.2,
                        padding: "0 0.15rem",
                        margin: 0,
                        boxShadow: "none",
                        outline: "none",
                      }}
                      value={footerActiveBranchId || ""}
                      onChange={handleFooterBranchChange}
                      disabled={isSwitchingBranch}
                      aria-label="Active branch"
                      title="Active branch"
                    >
                      {footerBranches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name || `Branch ${b.id}`}
                        </option>
                      ))}
                    </select>
                  )}

                  <FooterFinancialYearControl
                    isFiscalYearLoading={isFiscalYearLoading}
                    fiscalYears={fiscalYears}
                    selectedFiscalYear={selectedFiscalYear}
                    formatFiscalYearShort={formatFiscalYearShort}
                    onFiscalYearChange={handleFiscalYearChange}
                  />

                  <LanguageSelector variant="footer" />

                  <FooterProfileMenu
                    profileImage={footerProfileImage}
                    profileLabel={footerProfileLabel}
                    onProfile={() => navigate("/myprofile")}
                    onSignOut={handleFooterSignOut}
                    onProfileImageError={() => setFooterProfileImage(null)}
                  />
                </div>
              </div>
            </div>
          </footer>
        </div>
      </div>
      <div
        className={`dark-transparent sidebartoggler ${isMiniSidebar ? "visible" : ""
          }`}
        onClick={toggleSidebar}
      ></div>

      <SignOutConfirmModal
        show={showSignOutModal}
        onHide={() => setShowSignOutModal(false)}
        onConfirm={confirmFooterSignOut}
      />
    </>
  );
};

const SwitchPage = ({
  masterGoldRate,
  masterSilverRate,
  showLiveRate,
  onLiveRateToggle,
  lastUpdateTime,
}) => {
  const [socket, setSocket] = useState(null);
  const [userData, setUserData] = useState(getUserData());
  const [showDeactivationModal, setShowDeactivationModal] = useState(false);

  // Function to check if user is deactivated
  const checkUserStatus = () => {
    const currentUser = getUserData();
    if (
      currentUser &&
      (currentUser.status === 2 ||
        currentUser.status === "2" ||
        currentUser.status === 2.0)
    ) {
      // console.log(
      //   "🔴 User is deactivated, showing modal. Status:",
      //   currentUser.status,
      // );
      setShowDeactivationModal(true);
      return true;
    }
    return false;
  };

  // Check if user is already deactivated on component mount
  useEffect(() => {
    checkUserStatus();
  }, []);

  // Listen for permission updates from other components
  useEffect(() => {
    const handlePermissionsUpdated = (event) => {
      const status = event?.detail?.status;
      if (status === 2 || status === "2" || status === 2.0) {
        // console.log("🔴 Status is 2, showing deactivation modal");
        setShowDeactivationModal(true);
      }
    };

    window.addEventListener("permissionsUpdated", handlePermissionsUpdated);

    return () => {
      window.removeEventListener(
        "permissionsUpdated",
        handlePermissionsUpdated,
      );
    };
  }, []);

  // Periodically check user status from localStorage (in case of external updates)
  useEffect(() => {
    const intervalId = setInterval(() => {
      checkUserStatus();
    }, 2000); // Check every 2 seconds

    return () => clearInterval(intervalId);
  }, []);

  const masterGoldRateValue = Array.isArray(masterGoldRate)
    ? Number(masterGoldRate[0] || 0)
    : Number(masterGoldRate || 0);
  // Socket.IO listener for real-time updates
  useEffect(() => {
    // Only initialize socket if user is logged in
    // (support both legacy encrypted token and new ERP token)
    if (!localStorage.getItem("api-token") && !getErpToken()) {
      return;
    }

    const newSocket = createErpSocket();
    setSocket(newSocket);

    // Connection event logging
    newSocket.on("connect", () => {
      // console.log("SwitchPage: Connected to socket server", newSocket.id);
    });

    newSocket.on("disconnect", (reason) => {
      // console.log("SwitchPage: Disconnected from socket server:", reason);
    });

    newSocket.on("connect_error", (error) => {
      // console.error("SwitchPage: Socket connection error:", error);
    });

    newSocket.on("employee_permission_updated", (data) => {
      // console.log("🔍 Socket event received:", data);
      applyRealtimeSessionFromAdminUserSocket(data);

      if (!data?.employee) return;

      const updatedEmployee = {
        ua_id: data.employee.id || data.employee.ua_id,
        name: data.employee.name || "",
        phone: data.employee.phone || "",
        type: parseInt(data.employee.usertype || data.employee.type) || 0,
        status: parseInt(data.employee.status) || 0,
        email: data.employee.email || "",
      };

      // Check if the updated employee is the currently logged-in user
      if (
        updatedEmployee.ua_id === userData?.id ||
        updatedEmployee.ua_id === userData?.ua_id
      ) {
        // Update logged-in user's data for real-time permission changes
        const updatedUserData = {
          ...userData,
          usertype: updatedEmployee.type,
          status: updatedEmployee.status,
        };

        setUserData(updatedUserData);

        // Also update the persistent storage so changes persist after page refresh
        try {
          // Get current user data and update only permission fields
          const currentUserData = getUserData();
          if (currentUserData) {
            const updatedStorageData = {
              ...currentUserData,
              usertype: updatedEmployee.type,
              status: updatedEmployee.status,
            };

            // Re-encrypt and store using the same method as getUserData/setUserData
            localStorage.setItem("userData", encryptData(updatedStorageData));

            // CRITICAL: Extract and update permissions from socket data
            const permissionFields = [
              "daily_price",
              "bank",
              "supplier",
              "employee",
              "customer_arrival",
              "customer_assign",
              "customer",
              "metal_group",
              "item_category",
              "product_group",
              "stock",
              "stock_table",
              "edit_stock",
              "bill_type",
              "sales",
              "invoice",
              "edit_invoice",
              "karigar",
              "custom_order_entry",
              "custom_order_entry_table",
              "add_settlement",
              "bulk_stock",
              "repairing_order",
              "order_queue",
              "sales_target_view",
              "sales_target_edit",
              "hrms_holiday_view",
              "hrms_holiday_edit",
              "hrms_profile_view",
              "hrms_profile_edit",
              "hrms_incentive_view",
              "hrms_incentive_edit",
              "hrms_incentive_approve",
              "hrms_overtime_view",
              "hrms_overtime_edit",
              "hrms_overtime_approve",
              "hrms_advance_view",
              "hrms_advance_edit",
              "hrms_payroll_run",
              "hrms_payroll_view",
              "hrms_payroll_lock",
              "hrms_attendance_view",
              "hrms_attendance_edit",
              "dashboard",
              "purchase_table",
              "add_purchase",
              "edit_purchase",
              "metal_purchase_table",
              "add_old_purchase",
              "edit_old_purchase",
              "edit_custom_order_entry",
              "core_products",
              "product_assorting_table",
              "repairing_order_entry",
              "edit_repairing_order",
              "jangad",
              "add_jangad",
              "edit_jangad",
              "refinery",
              "add_refinery",
              "edit_refinery",
              "overall_ledger",
              "purchase_ledger",
              "urd_ledger",
              "sales_ledger",
              "karigar_ledger",
              "bank_ledger",
              "customer_ledger",
              "girvi",
              "girvitable",
              "addgirvi",
              "editgirvi",
              "girviquickpayment",
              "girvibankdetails",
              "girvibankledger",
            ];

            // Extract permissions from socket data
            const updatedPermissions = {};
            permissionFields.forEach((field) => {
              if (data.employee[field] !== undefined) {
                updatedPermissions[field] = data.employee[field];
              }
            });

            // console.log(
            //   "📋 Extracted Permissions from Socket:",
            //   updatedPermissions,
            // );

            // IMPORTANT: Merge new permissions with existing ones instead of replacing
            const existingPermissions = getUserPermissions() || {};
            const mergedPermissions = {
              ...existingPermissions,
              ...updatedPermissions,
            };

            // console.log("✅ Merged Permissions (Final):", mergedPermissions);
            // console.log("🔹 Girvi Permissions Specifically:", {
            //   girvi: mergedPermissions.girvi,
            //   girvitable: mergedPermissions.girvitable,
            //   addgirvi: mergedPermissions.addgirvi,
            //   editgirvi: mergedPermissions.editgirvi,
            //   girviquickpayment: mergedPermissions.girviquickpayment,
            //   girvibankdetails: mergedPermissions.girvibankdetails,
            //   girvibankledger: mergedPermissions.girvibankledger,
            // });

            // Update permissions in localStorage
            setUserPermissions(mergedPermissions);
            // console.log("💾 Permissions Saved to localStorage");

            // Dispatch custom event to notify all components to refresh permissions
            window.dispatchEvent(
              new CustomEvent("permissionsUpdated", {
                detail: {
                  usertype: updatedEmployee.type,
                  status: updatedEmployee.status,
                  permissions: updatedPermissions,
                },
              }),
            );

            // Check if user has been deactivated (status = 2)
            // console.log(
            //   "🔍 Checking updated employee status:",
            //   updatedEmployee.status,
            //   typeof updatedEmployee.status,
            // );
            if (
              updatedEmployee.status === 2 ||
              updatedEmployee.status === "2" ||
              updatedEmployee.status === 2.0
            ) {
              // console.log("🔴 User status is 2, showing deactivation modal");
              setShowDeactivationModal(true);
            }
          }
        } catch (error) {
          // console.error("Error updating stored user data:", error);
        }
      }
    });

    return () => {
      newSocket.off("employee_permission_updated");
      newSocket.disconnect();
    };
  }, [userData]);

  // Function to get the first allowed page
  const getFirstAllowedPage = () => {
    // NEW ERP auth: if we have an ERP token, treat user as logged-in and send to dashboard
    const erpToken = getErpToken();
    // console.log("getFirstAllowedPage Debug:");
    // console.log("- ERP token exists:", !!erpToken);

    if (erpToken) {
      // console.log("- Has ERP token, redirecting to /dashboard");
      return "/dashboard";
    }

    const permissions = getUserPermissions();
    // console.log("- Legacy permissions:", permissions);

    if (!permissions) {
      // console.log("- No permissions found, redirecting to /login");
      return "/login";
    }

    // Check if user has any actual permissions (not just an empty object)
    const hasAnyPermission = Object.values(permissions).some(
      (permission) => permission === 1 || permission === "1",
    );
    // console.log("- Has any permission:", hasAnyPermission);

    // If user has no permissions, redirect to access restricted page
    if (!hasAnyPermission) {
      // console.log("- No valid permissions, redirecting to /accessrestriced");
      return "/accessrestriced";
    }

    // Dashboard is accessible to all authenticated users with permissions
    // console.log("- Has permissions, redirecting to /dashboard");
    return "/dashboard";
  };

  // Handle user logout when deactivated
  const handleDeactivationLogout = () => {
    // Clear all localStorage data
    localStorage.removeItem("token");
    localStorage.removeItem("userPermissions");
    localStorage.removeItem("userData");
    localStorage.removeItem("keepLoggedIn");
    localStorage.removeItem("layoutType");
    localStorage.removeItem("currentModule");
    localStorage.removeItem("buttonTableStyle");
    localStorage.removeItem("currentPinType");
    localStorage.removeItem("accessPinModalState");
    localStorage.removeItem("accessPinModalReason");

    // Close modal and redirect to login
    setShowDeactivationModal(false);
    window.location.href = "/login";
  };

  return (
    <IdleProvider>
      <BrowserRouter>
        <AnalyticsTracker />
        <Routes>
          {/* Auth routes - no layout elements */}
          <Route path="/SignUp" element={<SignUp />} />
          <Route path="/Login" element={<Login />} />
          <Route path="/otp" element={<OTP />} />
          <Route path="/accesspin" element={<AccessPIN />} />
          <Route path="/forgetpassword" element={<ForgetPassword />} />
          <Route path="/resetpassword" element={<ForgetPassword />} />
          <Route
            path="/resetpassword/:encodedEmail"
            element={<ForgetPassword />}
          />
          <Route path="/accessrestriced" element={<AdminApproval />} />
          <Route
            path="/permissionrestricted"
            element={<PermissionRestriced />}
          />

          {/* Default route - redirect to first allowed page if logged in, otherwise to login */}
          <Route
            path="/"
            element={(() => {
              const legacyToken = localStorage.getItem("token");
              const erpToken = getErpToken();
              const hasToken = legacyToken || erpToken;

              // console.log("SwitchPage Default Route Debug:");
              // console.log("- Legacy token exists:", !!legacyToken);
              // console.log("- ERP token exists:", !!erpToken);
              // console.log("- Has any token:", !!hasToken);
              // console.log(
              //   "- Redirecting to:",
              //   hasToken ? getFirstAllowedPage() : "/login",
              // );

              return hasToken ? (
                <Navigate to={getFirstAllowedPage()} replace />
              ) : (
                <Navigate to="/login" replace />
              );
            })()}
          />

          {/* Protected routes - with layout */}
          <Route
            element={
              <ProtectedRoute>
                <IdleTimeoutWrapper>
                  <MainLayout
                    masterGoldRate={masterGoldRate}
                    masterSilverRate={masterSilverRate}
                    showLiveRate={showLiveRate}
                    onLiveRateToggle={onLiveRateToggle}
                    lastUpdateTime={lastUpdateTime}
                  />
                </IdleTimeoutWrapper>
              </ProtectedRoute>
            }
          >
            <Route path="/customerform" element={<CustomerForm />} />
            <Route path="/custheader" element={<CustHeader />} />
            <Route path="/sidebar" element={<Sidebar />} />
            <Route path="/navbar" element={<Navbar />} />
            <Route
              path="/customerwalkin"
              element={
                <ProtectedRoute
                  requiredPermission={PERMISSIONS.CUSTOMER_ARRIVAL}
                >
                  <TestCustomerForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/testcustomerform"
              element={<Navigate to="/customerwalkin" replace />}
            />
            <Route
              path="/customerassigntable"
              element={
                <ProtectedRoute
                  requiredPermission={PERMISSIONS.CUSTOMER_ASSIGN}
                >
                  <CustomerAssignTable
                    masterGoldRate={masterGoldRateValue}
                    masterSilverRate={masterSilverRate}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quote/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads"
              element={
                <ProtectedRoute>
                  <LeadsTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads/:leadId"
              element={
                <ProtectedRoute>
                  <LeadDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/followups"
              element={
                <ProtectedRoute>
                  <FollowUps />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reminders"
              element={
                <ProtectedRoute>
                  <RemindersQueue />
                </ProtectedRoute>
              }
            />
            <Route
              path="/overallledger"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.OVERALL_LEDGER}>
                  <OverallLedger />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rojmel"
              element={
                <ProtectedRoute
                  requiredAnyPermissions={ROJMEL_ROUTE_ANY_PERMISSIONS}
                >
                  <Rojmel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bankdetailstable"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.BANK}>
                  <BankDetailsTable />
                </ProtectedRoute>
              }
            />

            <Route
              path="/capitalinvestment"
              element={
                <ProtectedRoute
                  requiredPermission={PERMISSIONS.CAPITAL_INVESTMENT_TABLE}
                >
                  <CapitalInvestmentTable />
                </ProtectedRoute>
              }
            />

            <Route
              path="/tenantbranch"
              element={
                <ProtectedRoute
                  requiredAnyPermissions={[
                    PERMISSIONS.TENANT_TABLE,
                    PERMISSIONS.BRANCH_TABLE,
                  ]}
                >
                  <TenantBranchTable />
                </ProtectedRoute>
              }
            />

            <Route
              path="/metalgrouptable"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.METAL_GROUP}>
                  <MetalGroupTable />
                </ProtectedRoute>
              }
            />

            <Route
              path="/itemcategorytable"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.ITEM_CATEGORY}>
                  <ItemCategoryTable />
                </ProtectedRoute>
              }
            />

            <Route
              path="/roletable"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.EMPLOYEE}>
                  <RoleTable />
                </ProtectedRoute>
              }
            />

            <Route
              path="/addrole"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.EMPLOYEE}>
                  <AddRole />
                </ProtectedRoute>
              }
            />

            <Route
              path="/editrole"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.EMPLOYEE}>
                  <EditRole />
                </ProtectedRoute>
              }
            />

            <Route
              path="/suppliertable"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.SUPPLIER_TABLE}>
                  <SupplierTable
                    masterGoldRate={masterGoldRate}
                    masterSilverRate={masterSilverRate}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/karigar"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.SUPPLIER}>
                  <Karigar
                    masterGoldRate={masterGoldRateValue}
                    masterSilverRate={masterSilverRate}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/karigarledger"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.KARIGAR_LEDGER}>
                  <KarigarLedger
                    masterGoldRate={masterGoldRateValue}
                    masterSilverRate={masterSilverRate}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bankledger"
              element={
                <ProtectedRoute
                  requiredPermission={PERMISSIONS.GIRVIBANKLEDGER}
                >
                  <BankLedger />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customorderentry"
              element={
                <ProtectedRoute
                  requiredPermission={PERMISSIONS.CUSTOM_ORDER_ENTRY}
                >
                  <CustomOrderEntry
                    masterGoldRate={masterGoldRate}
                    masterSilverRate={masterSilverRate}
                  />
                </ProtectedRoute>
              }
            />

            <Route
              path="/customertable"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.CUSTOMER}>
                  <CustomerTable />
                </ProtectedRoute>
              }
            />

            <Route
              path="/customerledger"
              element={
                <ProtectedRoute
                  requiredPermission={PERMISSIONS.CUSTOMER_LEDGER}
                >
                  <CustomerLedger />
                </ProtectedRoute>
              }
            />

            <Route
              path="/salesledger"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.SALES_LEDGER}>
                  <SalesLedger />
                </ProtectedRoute>
              }
            />

            <Route
              path="/purchaseledger"
              element={
                <ProtectedRoute
                  requiredPermission={PERMISSIONS.PURCHASE_LEDGER}
                >
                  <PurchaseLedger />
                </ProtectedRoute>
              }
            />

            <Route
              path="/urdledger"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.URD_LEDGER}>
                  <UrdLedger />
                </ProtectedRoute>
              }
            />

            <Route
              path="/stocktable"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.STOCK_TABLE}>
                  <StockTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/huidbarcodesearch"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.STOCK_TABLE}>
                  <HuidBarcodeSearch />
                </ProtectedRoute>
              }
            />
            <Route
              path="/printbarcode"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.STOCK_TABLE}>
                  <PrintBarcode />
                </ProtectedRoute>
              }
            />

            <Route
              path="/productgrouptable"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCT_GROUP}>
                  <ProductGroupTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/productassortingtable"
              element={
                <ProtectedRoute
                  requiredPermission={PERMISSIONS.PRODUCT_ASSORTING_TABLE}
                >
                  <ProductAssortingTable
                    masterGoldRate={masterGoldRateValue}
                    masterSilverRate={masterSilverRate}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/stock-check"
              element={
                <ProtectedRoute
                  requiredPermission={PERMISSIONS.PRODUCT_ASSORTING_TABLE}
                >
                  <StockCheck />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoice"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.INVOICE}>
                  <Invoice />
                </ProtectedRoute>
              }
            />
            <Route
              path="/editinvoice"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.EDIT_INVOICE}>
                  <EditInvoice
                    masterGoldRate={masterGoldRate}
                    masterSilverRate={masterSilverRate}
                    showLiveRate={showLiveRate}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dailyprice"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.DAILY_PRICE}>
                  <DailyPrice />
                </ProtectedRoute>
              }
            />
            <Route
              path="/billtypetable"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.BILL_TYPE}>
                  <BillTypeTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/shopexpense"
              element={
                <ProtectedRoute>
                  <ShopExpenseRoute />
                </ProtectedRoute>
              }
            />
            <Route
              path="/branchexpense"
              element={
                <ProtectedRoute>
                  <ShopExpenseRoute />
                </ProtectedRoute>
              }
            />
            <Route
              path="/salestarget"
              element={
                <ProtectedRoute
                  requiredAnyPermissions={[
                    PERMISSIONS.SALES_TARGET_VIEW,
                    PERMISSIONS.SALES_TARGET_EDIT,
                  ]}
                >
                  <SalesTarget />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hrms"
              element={
                <ProtectedRoute
                  requiredAnyPermissions={HRMS_ROUTE_ANY_PERMISSIONS}
                >
                  <HrmsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.EMPLOYEE}>
                  <Employee />
                </ProtectedRoute>
              }
            />
            <Route
              path="/editemployee"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.EMPLOYEE}>
                  <EditEmployee />
                </ProtectedRoute>
              }
            />
            <Route
              path="/myprofile"
              element={
                <ProtectedRoute>
                  <MyProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/repairingsales"
              element={
                <ProtectedRoute
                  requiredPermission={PERMISSIONS.REPAIRING_SALES}
                >
                  <RepairingSales
                    masterGoldRate={masterGoldRateValue}
                    masterSilverRate={masterSilverRate}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/purchasetable"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.PURCHASE_TABLE}>
                  <PurchaseTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/metalpurchasetable"
              element={
                <ProtectedRoute
                  requiredPermission={PERMISSIONS.METAL_PURCHASE_TABLE}
                >
                  <MetalPurchaseTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/addoldpurchase"
              element={
                <ProtectedRoute
                  requiredPermission={PERMISSIONS.ADD_OLD_PURCHASE}
                >
                  <AddOldPurchase
                    masterGoldRate={masterGoldRateValue}
                    masterSilverRate={masterSilverRate}
                    showLiveRate={showLiveRate}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/editoldpurchase"
              element={
                <ProtectedRoute
                  requiredPermission={PERMISSIONS.EDIT_OLD_PURCHASE}
                >
                  <EditOldPurchase
                    masterGoldRate={masterGoldRateValue}
                    masterSilverRate={masterSilverRate}
                    showLiveRate={showLiveRate}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/oldpurchasepdf"
              element={
                <ProtectedRoute
                  requiredPermission={PERMISSIONS.OLD_PURCHASE_PDF}
                >
                  <OldPurchasePDFView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/metaltransfer"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.STOCK_TABLE}>
                  <MetalTransfer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/refinery"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.REFINERY}>
                  <RefineryTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/refinery/add"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.ADD_REFINERY}>
                  <AddRefinery />
                </ProtectedRoute>
              }
            />
            <Route
              path="/refinery/:id/edit"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.EDIT_REFINERY}>
                  <EditRefinery />
                </ProtectedRoute>
              }
            />
            <Route
              path="/refinery/:id"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.REFINERY}>
                  <ViewRefinery />
                </ProtectedRoute>
              }
            />
            <Route
              path="/addpurchase"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.ADD_PURCHASE}>
                  <AddPurchase
                    masterGoldRate={masterGoldRate}
                    masterSilverRate={masterSilverRate}
                    showLiveRate={showLiveRate}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/purchaseratecut"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.ADD_PURCHASE}>
                  <PurchaseRateCut
                    masterGoldRate={masterGoldRate}
                    masterSilverRate={masterSilverRate}
                    showLiveRate={showLiveRate}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/editpurchase"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.EDIT_PURCHASE}>
                  <EditPurchase
                    masterGoldRate={masterGoldRate}
                    masterSilverRate={masterSilverRate}
                    showLiveRate={showLiveRate}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.SALES}>
                  <Sales
                    masterGoldRate={masterGoldRate}
                    masterSilverRate={masterSilverRate}
                    showLiveRate={showLiveRate}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/editstock"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.EDIT_STOCK}>
                  <EditStock
                    masterGoldRate={masterGoldRateValue}
                    masterSilverRate={masterSilverRate}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/addstock"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.STOCK}>
                  <AddStock
                    masterGoldRate={masterGoldRateValue}
                    masterSilverRate={masterSilverRate}
                    showLiveRate={showLiveRate}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bulkstock"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.BULK_STOCK}>
                  <BulkStock
                    masterGoldRate={masterGoldRateValue}
                    masterSilverRate={masterSilverRate}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/addsettlement"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.ADD_SETTLEMENT}>
                  <AddSettlement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customorderentrytable"
              element={
                <ProtectedRoute
                  requiredPermission={PERMISSIONS.CUSTOM_ORDER_ENTRY_TABLE}
                >
                  <CustomOrderEntryTable
                    masterGoldRate={masterGoldRateValue}
                    masterSilverRate={masterSilverRate}
                  />
                </ProtectedRoute>
              }
            />
            {/* <Route
            path="/editcustomorderentry"
            element={
              <ProtectedRoute
                requiredPermission={PERMISSIONS.CUSTOM_ORDER_ENTRY_TABLE}
              >
                <EditCustomOrderEntry />
              </ProtectedRoute>
            }
          /> */}
            <Route
              path="/editcustomorderentry"
              element={
                <ProtectedRoute
                  requiredPermission={PERMISSIONS.CUSTOM_ORDER_ENTRY_TABLE}
                >
                  <EditCustomOrderEntry
                    masterGoldRate={masterGoldRateValue}
                    masterSilverRate={masterSilverRate}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customorderdetails/:id"
              element={
                <ProtectedRoute
                  requiredPermission={PERMISSIONS.CUSTOM_ORDER_ENTRY_TABLE}
                >
                  <NavigateCustomOrderDetailToEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orderqueue"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.ORDER_QUEUE}>
                  <OrderQueue />
                </ProtectedRoute>
              }
            />
            <Route
              path="/repairingorder"
              element={
                <ProtectedRoute
                  requiredPermission={PERMISSIONS.REPAIRING_ORDER}
                >
                  <RepairingOrder
                    masterGoldRate={masterGoldRateValue}
                    masterSilverRate={masterSilverRate}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/repairingorderentry"
              element={
                <ProtectedRoute
                  requiredPermission={PERMISSIONS.REPAIRING_ORDER_ENTRY}
                >
                  <AddRepairingOrder
                    masterGoldRate={masterGoldRateValue}
                    masterSilverRate={masterSilverRate}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/editrepairingorder"
              element={
                <ProtectedRoute
                  requiredPermission={PERMISSIONS.EDIT_REPAIRING_ORDER}
                >
                  <EditRepairingOrder
                    masterGoldRate={masterGoldRateValue}
                    masterSilverRate={masterSilverRate}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/repairingorderdetails/:id"
              element={
                <ProtectedRoute
                  requiredPermission={PERMISSIONS.REPAIRING_ORDER}
                >
                  <NavigateRepairingOrderDetailToEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/Jangad"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.JANGAD}>
                  <JangadTable
                    masterGoldRate={masterGoldRateValue}
                    masterSilverRate={masterSilverRate}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/addjangad"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.ADD_JANGAD}>
                  <AddJangad
                    masterGoldRate={masterGoldRateValue}
                    masterSilverRate={masterSilverRate}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/editJangad"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.EDIT_JANGAD}>
                  <EditJangad
                    masterGoldRate={masterGoldRateValue}
                    masterSilverRate={masterSilverRate}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employeeselfservice"
              element={
                <ProtectedRoute>
                  <EmployeeSelfService />
                </ProtectedRoute>
              }
            />

            {/* WhatsApp Business module */}
            <Route
              path="/whatsapp"
              element={
                <WhatsAppRoute>
                  <WhatsAppRootRedirect />
                </WhatsAppRoute>
              }
            />
            <Route
              path="/whatsapp/inbox"
              element={
                <WhatsAppRoute
                  requiredWhatsAppPermission={WHATSAPP_PERMISSION_CODES.INBOX}
                >
                  <WhatsAppInbox />
                </WhatsAppRoute>
              }
            />
            <Route
              path="/whatsapp/inbox/:chatId"
              element={
                <WhatsAppRoute
                  requiredWhatsAppPermission={WHATSAPP_PERMISSION_CODES.INBOX}
                >
                  <WhatsAppInbox />
                </WhatsAppRoute>
              }
            />
            <Route
              path="/whatsapp/phonebooks"
              element={
                <WhatsAppRoute
                  requiredWhatsAppPermission={
                    WHATSAPP_PERMISSION_CODES.PHONEBOOK
                  }
                >
                  <WhatsAppPhonebooks />
                </WhatsAppRoute>
              }
            />
            <Route
              path="/whatsapp/phonebooks/:id"
              element={
                <WhatsAppRoute
                  requiredWhatsAppPermission={
                    WHATSAPP_PERMISSION_CODES.PHONEBOOK
                  }
                >
                  <WhatsAppPhonebooks />
                </WhatsAppRoute>
              }
            />
            <Route
              path="/whatsapp/templates/create"
              element={
                <WhatsAppRoute
                  requiredWhatsAppPermission={
                    WHATSAPP_PERMISSION_CODES.TEMPLATE
                  }
                >
                  <WhatsAppTemplateCreate />
                </WhatsAppRoute>
              }
            />
            <Route
              path="/whatsapp/templates"
              element={
                <WhatsAppRoute
                  requiredWhatsAppPermission={
                    WHATSAPP_PERMISSION_CODES.TEMPLATE
                  }
                >
                  <WhatsAppTemplates />
                </WhatsAppRoute>
              }
            />
            <Route
              path="/whatsapp/campaigns/all"
              element={<Navigate to="/whatsapp/campaigns" replace />}
            />
            <Route
              path="/whatsapp/campaigns/send"
              element={
                <WhatsAppRoute
                  requiredWhatsAppPermission={WHATSAPP_PERMISSION_CODES.CAMPAIGN}
                >
                  <WhatsAppSendCampaign />
                </WhatsAppRoute>
              }
            />
            <Route
              path="/whatsapp/campaigns/new"
              element={<Navigate to="/whatsapp/campaigns/send" replace />}
            />
            <Route
              path="/whatsapp/campaigns"
              element={
                <WhatsAppRoute
                  requiredWhatsAppPermission={WHATSAPP_PERMISSION_CODES.CAMPAIGN}
                >
                  <WhatsAppCampaigns />
                </WhatsAppRoute>
              }
            />
            <Route
              path="/whatsapp/campaigns/:id"
              element={
                <WhatsAppRoute
                  requiredWhatsAppPermission={WHATSAPP_PERMISSION_CODES.CAMPAIGN}
                >
                  <WhatsAppCampaignDetail />
                </WhatsAppRoute>
              }
            />
            <Route
              path="/whatsapp/dashboard"
              element={
                <WhatsAppRoute
                  requiredWhatsAppPermission={
                    WHATSAPP_PERMISSION_CODES.DASHBOARD
                  }
                >
                  <WhatsAppDashboard />
                </WhatsAppRoute>
              }
            />

            {/* Girvi Routes */}
            <Route
              path="/girvi"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.GIRVI}>
                  <GirviDashboard
                    masterGoldRate={masterGoldRate}
                    masterSilverRate={masterSilverRate}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/addgirvi"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.ADDGIRVI}>
                  <AddGirvi
                    masterGoldRate={masterGoldRate}
                    masterSilverRate={masterSilverRate}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/girvitable"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.GIRVITABLE}>
                  <GirviTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="girviquickpayment"
              element={
                <ProtectedRoute
                  requiredPermission={PERMISSIONS.GIRVIQUICKPAYMENT}
                >
                  <GirviQuickPayment />
                </ProtectedRoute>
              }
            />
            <Route
              path="girvibankdetails"
              element={
                <ProtectedRoute
                  requiredPermission={PERMISSIONS.GIRVIBANKDETAILS}
                >
                  <GirviBankDetails />
                </ProtectedRoute>
              }
            />

            <Route
              path="/girvi/payment-entry"
              element={
                <ProtectedRoute>
                  <GirviPaymentEntry />
                </ProtectedRoute>
              }
            />
            <Route
              path="/editgirvi"
              element={
                <ProtectedRoute requiredPermission={PERMISSIONS.EDITGIRVI}>
                  <EditGirvi
                    masterGoldRate={masterGoldRate}
                    masterSilverRate={masterSilverRate}
                  />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Catch all - redirect to login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>

      {/* Deactivation Modal */}
      <AccountDeactivatedModal
        show={showDeactivationModal}
        onConfirm={handleDeactivationLogout}
      />
    </IdleProvider>
  );
};

export default SwitchPage;
